import { useState, useEffect } from "react";
import ReactGA from "react-ga4";
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { ContactAPI, CategoryAPI } from "../../../api";

const initialData = {
	full_name: "",
	phone_number: "",
	email_address: "",
	service: "",
};

const ContactForm = () => {
	const [formData, setFormData] = useState(initialData);
	const [categories, setCategories] = useState([]);
	const [success, setSuccess] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		CategoryAPI.getAllCategories()
			.then((res) => setCategories(res.data))
			.catch((err) => console.error(err));
	}, []);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handlePhoneChange = (phone) => {
		const parsedPhone = parsePhoneNumberFromString(phone);
		if (parsedPhone && parsedPhone.isValid()) {
			setFormData((prev) => ({
				...prev,
				phone_number: parsedPhone.formatInternational(),
			}));
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		try {
			await ContactAPI.submitContactForm(formData);
			ReactGA.event({
				category: "Contact Form",
				action: "contact_form_submitted",
				label: formData.service,
			});
			setSuccess(true);
			setFormData(initialData);
		} catch (err) {
			console.error(err);
			alert("Something went wrong. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	if (success) {
		return (
			<div className="bg-white rounded-2xl p-12 shadow-xl text-center space-y-6 animate-fade-in">
				<div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						className="h-10 w-10"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={3}
							d="M5 13l4 4L19 7"
						/>
					</svg>
				</div>
				<h2 className="text-2xl font-bold text-gray-900">Message Sent!</h2>
				<p className="text-gray-500 leading-relaxed">
					Thank you for reaching out. Your request has been received, and our
					logistics team will get back to you shortly.
				</p>
				<button
					onClick={() => setSuccess(false)}
					className="text-brand-600 cursor-pointer font-bold uppercase tracking-widest text-sm ha:underline"
				>
					Send another message
				</button>
			</div>
		);
	}

	return (
		<div className="bg-white rounded-2xl p-8 shadow-xl">
			<form onSubmit={handleSubmit} className="space-y-5">
				<p className="text-gray-500 text-sm mb-6 border-l-4 border-brand-500 pl-4">
					Please fill out the form and we will get in touch with you shortly.
				</p>

				{[
					{ label: "Full Name", name: "full_name", type: "text" },
					{ label: "Phone Number", name: "phone_number", type: "phone" },
					{ label: "Email Address", name: "email_address", type: "email" },
				].map((field) => (
					<div key={field.name} className="flex flex-col space-y-2">
						<span className="text-xs font-bold uppercase tracking-wider text-gray-400">
							{field.label}
						</span>
						{field.type === "phone" ? (
							<PhoneInput
								defaultCountry="ae"
								value={formData[field.name]}
								onChange={handlePhoneChange}
								disabled={loading}
								inputClassName="!w-full !px-4 !py-3 !bg-transparent !border-none !outline-none disabled:!opacity-50"
								className="w-full bg-gray-50 border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-transparent transition-all disabled:opacity-50"
							/>
						) : (
							<input
								type={field.type}
								name={field.name}
								value={formData[field.name]}
								onChange={handleChange}
								disabled={loading}
								required
								className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all disabled:opacity-50"
							/>
						)}
					</div>
				))}

				<div className="flex flex-col space-y-2">
					<span className="text-xs font-bold uppercase tracking-wider text-gray-400">
						Choose a service
					</span>
					<div className="relative">
						<select
							name="service"
							value={formData.service}
							onChange={handleChange}
							disabled={loading}
							required
							className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none appearance-none disabled:opacity-50"
						>
							<option value="">Choose Service</option>
							{categories.map((c, i) => (
								<option key={i} value={c.title}>
									{c.title}
								</option>
							))}
						</select>
						<div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-gray-400">
							<svg
								className="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									d="M19 9l-7 7-7-7"
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
								/>
							</svg>
						</div>
					</div>
				</div>

				<button
					type="submit"
					disabled={loading}
					className="group cursor-pointer relative w-full py-4 bg-brand-600 ha:bg-brand-700 text-white font-bold uppercase tracking-widest rounded-lg shadow-lg ha:shadow-brand-500/20 transition-all transform active:scale-95 disabled:bg-brand-400 disabled:cursor-not-allowed overflow-hidden"
				>
					<span
						className={`flex items-center justify-center transition-all ${loading ? "opacity-0" : "opacity-100"}`}
					>
						Submit Request
					</span>

					{loading && (
						<div className="absolute inset-0 flex items-center justify-center">
							<svg
								className="animate-spin h-6 w-6 text-white"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
							>
								<circle
									className="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									strokeWidth="4"
								></circle>
								<path
									className="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								></path>
							</svg>
						</div>
					)}
				</button>
			</form>
		</div>
	);
};

export default ContactForm;
